import { WmButton } from "@watermarkinsights/ripple-react";
import { useReducer, useEffect } from "react";
import translate from "../../i18n/translate";
import { downloadPDF } from "../common/helper";
import { sendRequest, sendRequestWithPayload } from "../../common/http";
import { domToCanvas } from "modern-screenshot";
import { useIntl } from "react-intl";

export interface HeaderProps {
  title: string;
}

interface ExportResultSet {
  pdfFileBytes: string;
}
interface TokenResultSet {
  token: string;
}
interface HeaderAction {
  type: string;
  token: string | null;
  data: boolean;
}

const defaultHeaderAction = {
  type: "",
  token: "",
  data: false
};

interface HeaderState {
  token: string | null;
  isExportInProgress: boolean;
}

const reducer = (state: HeaderState, action: HeaderAction) => {
  switch (action.type) {
    case "setToken":
      return { ...state, token: action.token };
    case "setExportInProgress":
      return { ...state, isExportInProgress: action.data };
    default:
      return state;
  }
};

const initialState = {
  token: null,
  isExportInProgress: false
};

export default function Header({ title }: HeaderProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const header = useIntl().formatMessage({
    id: "app.lightcast.regionLabel"
  });

  const exportToPdf = (
    token: string,
    applyResult: (result: ExportResultSet) => void,
    onError: (error: string) => void
  ) => {
    let mainContent = document.getElementById("maincontent");
    focusOnDownloadMessage();

    if (mainContent) {
      let clone = mainContent.cloneNode(true);

      let headerSection = clone.firstChild;
      headerSection && clone.removeChild(headerSection);

      let lfbElement =
        (document.querySelector("#lfb-chart") as HTMLCanvasElement) ||
        (document.querySelector("#api-error") as HTMLCanvasElement);

      let wrapperDiv = document.createElement("div");

      let canvasNode: Node | null = null;
      const traverseNode = (node: Node): Node => {
        let nodeElement = node as HTMLElement;
        if (nodeElement && nodeElement.id && nodeElement.id === "lfb-chart") {
          canvasNode = node;
        }
        if (node.hasChildNodes()) {
          for (const childNode of node.childNodes) {
            traverseNode(childNode);
            if (canvasNode) break;
          }
        }
        return node;
      };

      let el = clone.firstChild;
      while (el && el.hasChildNodes()) {
        traverseNode(el);
        el = el.nextSibling;
      }

      if (lfbElement) {
        domToCanvas(lfbElement).then((canvas) => {
          let imgWrapper = document.createElement("div");
          const img = document.createElement("img");
          img.src = canvas.toDataURL("image/png");
          imgWrapper.setAttribute("id", "lfb-chart-img");
          imgWrapper.appendChild(img);

          if (canvasNode && canvasNode.parentNode) {
            canvasNode.parentNode.replaceChild(imgWrapper, canvasNode);
          }

          wrapperDiv.appendChild(clone);

          let formData = new FormData();
          formData.append("html", wrapperDiv.outerHTML);
          formData.append("_csrf_token", token);
          formData.append("sub_header", encodeURIComponent(header));
          formData.append("export_id", "labor-market-insights-export");
          formData.append("header", encodeURIComponent(title));

          sendRequestWithPayload(
            {
              uri: "/export",
              camelize: true,
              body: formData
            },
            applyResult,
            onError
          );
        });
      }
    }
  };

  useEffect(() => {
    if (state.isExportInProgress) {
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        (result: TokenResultSet) => {
          dispatch({
            ...defaultHeaderAction,
            type: "setToken",
            token: result.token
          });
        }
      );
    }
  }, [state.isExportInProgress]);
  useEffect(() => {
    if (state.token && state.isExportInProgress) {
      const applyResult = (result: ExportResultSet) => {
        const fileTitle = "Labor_Market_Insights";
        downloadPDF(result.pdfFileBytes, fileTitle);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      const onError = (error: string) => {
        // Log error in console for debugging
        console.log("API Error:", error);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      exportToPdf(state.token, applyResult, onError);
    }
  }, [state.isExportInProgress, state.token]);

  return (
    <>
      <section
        className="page-banner-container -mainheader -withbreadcrumbs only-breadcrumbs header-padding"
        id="page-header"
      >
        <div className="flex-row export-container">
          <div>
            <div>
              <span className="org-name uppercase">{title}</span>
            </div>
            <div className="identity-details">
              <div className="page-header -titleonly">
                <h2 id="main-heading" className="plan-title-header">{header}</h2>
              </div>
            </div>
          </div>
          <div className="flex-row export-button">
            {state.isExportInProgress && (
              <span className="download-text">
                {translate("app.lightcast.exportPdf.downloadReportText")}
              </span>
            )}
            <div className="button-container">
              <WmButton
                button-type="secondary"
                icon="f1da"
                icon-size="20px"
                onClick={() =>
                  dispatch({
                    ...defaultHeaderAction,
                    type: "setExportInProgress",
                    data: true
                  })
                }
                disabled={state.isExportInProgress}
              >
                {translate("app.lightcast.exportPdf.download")}
              </WmButton>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function focusOnDownloadMessage() {
  let downloadText = document.querySelector(".download-text");
  if (downloadText) {
    downloadText.setAttribute("tabindex", "0");
    (downloadText as HTMLElement).focus();
  }
}
